<template>
  <AppLayout>
    <HeadlessListing
      :key="route.params.slug"
      :listing-slug="route.params.slug"
      @thanksClosed="formKey++"
    >
      <template
        #default="{
          onBid,
          listing,
          countdownTo,
          reloadListing,
          bids,
          activeBids,
          onCancelBid,
          bidError,
          cancelError,
          cancelling,
          states,
        }"
      >
        <div
          class="mx-auto max-w-7xl pt-2 md:pt-8 md:grid md:grid-cols-12 md:items-start md:gap-x-8 lg:gap-x-12"
        >
          <div class="md:col-span-5 md:sticky md:top-8">
            <div
              class="rounded-xl border border-boba-grey/50 bg-white p-3 shadow-sm aspect-[4/3] flex items-center justify-center overflow-hidden"
            >
              <img
                class="max-h-full max-w-full object-contain cursor-zoom-in rounded-lg"
                :src="currentImage || listing.images[0]"
                :alt="`${listing.title} Product Image`"
                @click="openDialog(currentImage || listing.images[0])"
              />
            </div>
            <image-carousel
              v-if="listing.images.length > 1"
              :images="listing.images"
              :current="currentImage || listing.images[0]"
              @choose="chooseImage"
            />
          </div>

          <dialog
            ref="imageDialog"
            class="bg-transparent max-w-[95vw] max-h-[80vh] md:-top-[10vh] overflow-hidden"
          >
            <button
              type="button"
              aria-label="Close"
              class="absolute aspect-square rounded-lg top-2 right-2 min-h-[44px] min-w-[44px] shadow bg-white flex justify-center items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-boba-brown"
              @click="closeDialog"
            >
              <i class="fa-solid fa-xmark text-boba-grey text-lg" />
            </button>
            <div
              class="bg-white border rounded-xl w-full md:h-full overflow-clip text-center"
            >
              <img
                :src="imageDetail"
                :alt="`${listing.title} Product Image`"
                class="w-auto max-h-[80vh] mx-auto object-cover rounded-xl"
              />
            </div>
          </dialog>

          <div class="mt-5 md:mt-0 md:col-span-7 min-w-0">
            <div>
              <p
                v-if="listing.subtitle"
                class="text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-boba-grey-dark"
              >
                {{ listing.subtitle }}
              </p>
              <h1
                class="mt-1 text-3xl md:text-4xl font-semibold tracking-tight leading-tight text-boba-brown text-balance"
              >
                {{ listing.title }}
              </h1>
            </div>

            <!-- Falling price: unchanged buy-now experience -->
            <live-bid-panel
              v-if="!listing.isLowBid()"
              :key="`live-${formKey}`"
              :listing="listing"
              :countdown-to="countdownTo"
              @bid="onBid"
              @reload="reloadListing"
            />

            <!-- Lowest clearing price: blind max-price bids -->
            <template v-else>
              <login v-if="listing.hasEnded()">
                <template #fallback="{ loginURI, register }">
                  <auction-results :listing="listing" :bids="[]" :signed-in="false">
                    <template #signin>
                      <sign-in-prompt
                        :login-u-r-i="loginURI"
                        message="Sign in to see your bids and what you won."
                        @register="register"
                      />
                    </template>
                  </auction-results>
                </template>
                <template #default>
                  <auction-results :listing="listing" :bids="bids" />
                </template>
              </login>

              <max-bid-panel
                v-else-if="!listing.isActive()"
                :listing="listing"
                :balance="0"
                :countdown-to="countdownTo"
                countdown-label="Begins in"
              >
                <template #form>
                  <p
                    class="mt-4 inline-flex items-center gap-2 text-sm text-boba-grey-dark"
                  >
                    <i class="fa-regular fa-clock" aria-hidden="true" />
                    Bidding opens when the auction begins.
                  </p>
                </template>
              </max-bid-panel>

              <login v-else>
                <template #fallback="{ loginURI, register }">
                  <max-bid-panel :listing="listing" :balance="0">
                    <template #form>
                      <p
                        v-if="listing.hasPublicFloor() && listing.minBid() !== null"
                        class="mt-4 text-sm text-boba-grey-dark"
                      >
                        Minimum bid
                        <span class="font-semibold text-boba-brown"
                          >{{ $format((listing.minBid() as number) / 1e9) }} miles</span
                        >
                        per unit
                      </p>
                      <sign-in-prompt
                        class="mt-4"
                        :login-u-r-i="loginURI"
                        message="Sign in to place a bid."
                        @register="register"
                      />
                    </template>
                  </max-bid-panel>
                </template>
                <template #default>
                  <max-bid-panel
                    :key="`max-${formKey}`"
                    :listing="listing"
                    :balance="authStore.balance.available"
                    :error="bidError"
                    :posting="states.bidPosting"
                    :disabled="!listing.isActive()"
                    @bid="onBid"
                    @ended="reloadListing"
                  />
                  <active-bids
                    :bids="activeBids"
                    :can-cancel="listing.isActive()"
                    :cancelling="cancelling"
                    :error="cancelError"
                    @cancel="onCancelBid"
                  />
                </template>
              </login>
            </template>

            <div class="mt-4 rounded-xl border border-boba-grey/50 bg-boba-sand-light shadow-sm px-5 py-5 md:px-8 md:py-6">
              <h2 class="text-xl md:text-2xl font-semibold tracking-tight text-boba-brown">
                Description
              </h2>
              <div
                class="prose prose-sm md:prose-base max-w-none mt-3 text-boba-brown prose-headings:text-boba-brown prose-a:text-boba-brown prose-a:underline-offset-2"
                v-html="listing.description"
              ></div>
            </div>
          </div>
        </div>
      </template>
    </HeadlessListing>
  </AppLayout>
</template>
<script lang="ts" setup>
import { ref } from "vue";
import { useRoute } from "vue-router";

import AppLayout from "@/components/Layouts/Application.vue";
import HeadlessListing from "@/components/Headless/Listing.vue";
import LiveBidPanel from "@/components/Listing/LiveBidPanel.vue";
import MaxBidPanel from "@/components/Listing/MaxBidPanel.vue";
import ActiveBids from "@/components/Listing/ActiveBids.vue";
import AuctionResults from "@/components/Listing/AuctionResults.vue";
import SignInPrompt from "@/components/Listing/SignInPrompt.vue";
import ImageCarousel from "@/components/ImageCarousel.vue";
import Login from "@/pkg/auth/components/Login.vue";
import { useAuth } from "@/pkg/auth/store";

const authStore = useAuth();
const route = useRoute();

let currentImage = ref<boolean | string>(false);
let imageDetail = ref<string>("");
const imageDialog = ref<HTMLDialogElement | null>(null);
/** Bumped after a completed bid so the form remounts with cleared inputs. */
const formKey = ref(0);

function chooseImage(img: string) {
  currentImage.value = img;
}

function openDialog(src: string) {
  imageDetail.value = src;
  imageDialog.value?.showModal();
}

function closeDialog() {
  imageDetail.value = "";
  imageDialog.value?.close();
}
</script>
